import _ from 'lodash';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Icon } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { colors } from '~/styles';
import { formatPeriod, pivotData } from './Pivot';

const S = {
  Row: styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0;
    opacity: 0;
    transition: opacity 150ms;
  `,

  ActionIcon: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border: none;
    border-radius: 0.25rem;
    background: none;
    padding: 0;
    color: ${({ $active, $danger }) => ($active ? ($danger ? colors.danger : colors.primary) : colors.grey40)};
    cursor: pointer;
    font-size: 0.6875rem;

    &:hover {
      background: none;
      color: ${colors.grey75};
    }
  `,
};

function tablePartToMarkdown(part) {
  const visibleFields = (part.fields || []).filter((f) => f.visible !== false);
  if (!visibleFields.length || !Array.isArray(part.data) || !part.data.length) return '';

  const header = visibleFields.map((f) => f.label).join(' | ');
  const separator = visibleFields.map((f) => (f.role === 'metric' ? '---:' : '---')).join(' | ');
  const rows = part.data.map((row) =>
    visibleFields
      .map((f) => {
        const val = _.get(row, f.path);
        if (val == null) return '';
        if (Array.isArray(val)) return val.map((v) => (typeof v === 'object' ? (v?.name ?? '') : String(v))).join(', ');
        if (typeof val === 'object') return val.name ?? val.title ?? val.label ?? '';
        return String(val);
      })
      .join(' | '),
  );
  return `| ${header} |\n| ${separator} |\n${rows.map((r) => `| ${r} |`).join('\n')}`;
}

function pivotPartToMarkdown(part) {
  // Mirror the in-pivot "Save CSV" output: rows are dim tuples, columns are period labels,
  // cells are the raw cellField value. Tools that override cell rendering still get a
  // useful copy because the underlying numeric value lives on every data row.
  const pivot = pivotData({
    data: part.data,
    fields: part.fields,
    meta: part.meta,
    cellValue: part.cellValue,
    tooltipConfig: part.tooltipConfig,
  });
  if (!pivot) return '';

  const { rows, rowDimFields, periods, periodUnit, cellField } = pivot;
  const headers = [...rowDimFields.map((f) => f.label), ...periods.map((p) => formatPeriod(p, periodUnit))];
  const separator = [...rowDimFields.map(() => '---'), ...periods.map(() => '---:')];
  const bodyRows = rows.map((row) => {
    const dimCells = row.dimValues.map((v) => (v == null ? '' : String(v)));
    const periodCells = periods.map((p) => {
      const v = row.cells.get(p)?.[cellField.key];
      return v == null ? '' : String(v);
    });
    return [...dimCells, ...periodCells];
  });

  return [
    `| ${headers.join(' | ')} |`,
    `| ${separator.join(' | ')} |`,
    ...bodyRows.map((r) => `| ${r.join(' | ')} |`),
  ].join('\n');
}

function partsToText(parts) {
  return parts
    .map((p) => {
      if (p.type === 'text') return p.content;
      if (p.type === 'table') return tablePartToMarkdown(p);
      if (p.type === 'pivot') return pivotPartToMarkdown(p);
      return null;
    })
    .filter(Boolean)
    .join('\n\n');
}

export default function ResponseActions({ message, conversationId }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const [feedback, setFeedback] = useState(message.feedback ?? null);
  const [copied, setCopied] = useState(false);

  const textContent = partsToText(message.parts);

  const handleFeedback = async (value) => {
    const newValue = feedback === value ? null : value;
    setFeedback(newValue);
    if (conversationId && message.id) {
      await api.www
        .workspaces(workspace.id)
        .ai()
        .conversations(conversationId)
        .messages(message.id)
        .updateFeedback({ feedback: newValue });
      if (newValue) toast.success('Thank you for your feedback!');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(textContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'response.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <S.Row className="response-actions">
      <S.ActionIcon $active={feedback === 1} onClick={() => handleFeedback(1)} title="Helpful">
        <Icon icon="thumbs-up" />
      </S.ActionIcon>
      <S.ActionIcon $active={feedback === -1} $danger onClick={() => handleFeedback(-1)} title="Not helpful">
        <Icon icon="thumbs-down" />
      </S.ActionIcon>
      <S.ActionIcon onClick={handleCopy} title={copied ? 'Copied!' : 'Copy'}>
        <Icon icon={copied ? 'check' : 'clipboard'} />
      </S.ActionIcon>
      <S.ActionIcon onClick={handleDownload} title="Download">
        <Icon icon="download" />
      </S.ActionIcon>
    </S.Row>
  );
}
